import cookie from "cookie";
import { getSelectorsByUserAgent } from "react-device-detect";
import { v4 as uuidv4 } from "uuid";

import {
    getClientInitializationValues,
    ClientOptions,
    Identifiers
} from "@atlassian/feature-gate-node-client-bootstrapper";

import {
    ATL_COHORT,
    AtlCohort,
    Cohort
} from "../analytics/atl-analytics/cohort";
import { polyfillGetRandomValuesIfNecessary } from "../utils/polyfills";
import { parseRoute } from "../wac-routes";

import { TARGET_APP } from "./initialization";
import { FeatureGatesBootstrapValues } from "./types";

export const ANALYTICS_ANONYMOUS_ID_COOKIE_KEY = "ajs_anonymous_id";
export const ATLASSIAN_ACCOUNT_ID_COOKIE_KEY = "__aid_user_id";
export let ATL_COHORT_COOKIE_VALUE: AtlCohort | undefined = undefined;
export let ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE: string | undefined = undefined;

const DEVICE_TYPE = "deviceType";

// @ts-ignore
const clientOptions: ClientOptions = {
    apiKey: String(process.env["FEATURE_GATES_KEY"]),
    products: []
};

export const getFeatureGateValues = async (
    request: Request
): Promise<FeatureGatesBootstrapValues> => {
    const requestHeaders = request.headers;
    const identifiers: Identifiers = getIdentifiers(requestHeaders);
    const customAttributes = getCustomAttributes(request);
    let response;
    try {
        const featureGateValues = await getClientInitializationValues(
            clientOptions,
            TARGET_APP,
            identifiers,
            customAttributes
        );
        response = {
            ...featureGateValues,
            identifiers
        };
    } catch (e) {
        const reason = e instanceof Error ? e.message : JSON.stringify(e);
        console.warn(
            `Could not fetch feature gate values, falling back to defaults. Reason: ${reason}`
        );
        response = {
            identifiers
        };
    }

    return response;
};

const getIdentifiers = (headers: Headers): Identifiers => {
    // @ts-ignore
    const cookieHeader = headers["cookie"];
    const parsedCookies = cookieHeader ? cookie.parse(cookieHeader) : {};

    return {
        analyticsAnonymousId: getOrGenerateAnalyticsAnonymousId(parsedCookies),
        atlassianAccountId: getAtlassianAccountId(parsedCookies)
    };
};

const getOrGenerateAnalyticsAnonymousId = (
    parsedCookies: Record<string, string>
): string => {
    const anonymousIdCookieValue =
        parsedCookies[ANALYTICS_ANONYMOUS_ID_COOKIE_KEY];

    if (anonymousIdCookieValue) {
        // The cookie value is wrapped in quotes, so we need to remove those.
        return decodeURIComponent(anonymousIdCookieValue).replace(/['"]+/g, "");
    }

    polyfillGetRandomValuesIfNecessary();
    ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE = uuidv4();
    return ANALYTICS_ANONYMOUS_ID_COOKIE_VALUE;
};

/**
 * Get AtlCohort from cookie, if it doesn't exist, generate a new one
 * @param parsedCookies
 * @returns {"bucketAll":{"bucketedAtUTC":"2024-04-24T19:26:55.734Z","version":"2","index":74,"bucketId":0}}
 */
const getOrGenerateAtlCohort = (
    parsedCookies: Record<string, string>
): AtlCohort => {
    const atlCohortCookieValue = parsedCookies[ATL_COHORT];
    if (atlCohortCookieValue) {
        try {
            return JSON.parse(atlCohortCookieValue);
        } catch (e) {
            console.warn(
                `Could not parse ATL_COHORT cookie value: ${atlCohortCookieValue}, creating a new one.`,
                e
            );
        }
    }
    const bucketAttribute = Cohort.createBucket("bucketAll");
    ATL_COHORT_COOKIE_VALUE = {
        bucketAll: bucketAttribute
    };
    return ATL_COHORT_COOKIE_VALUE;
};

const getAtlassianAccountId = (
    parsedCookies: Record<string, string>
): string | undefined => {
    const atlassianAccountIdCookieValue =
        parsedCookies[ATLASSIAN_ACCOUNT_ID_COOKIE_KEY];
    if (atlassianAccountIdCookieValue) {
        return decodeURIComponent(atlassianAccountIdCookieValue);
    }
    return undefined;
};

export type UserAttributes = {
    [ATL_COHORT]: number;
    [DEVICE_TYPE]: "mobile" | "tablet" | "desktop" | "other";
    platform: "uip";
    country: string;
    region: string;
    loginUserDetected: boolean;
    queryParams: string;
    clusterTag: string;
    requestPath: string;
    browserName: string;
    browserVersion: string;
    browserOS: string;
    referer: string;
    locale: string;
};

export type RequestOverride = {
    headers: Record<"cookieOverride" | "user-agent", string>;
    referrer?: string;
};

export const getCustomAttributes = (
    request: Request | RequestOverride
): UserAttributes => {
    const data: Partial<UserAttributes> = {};
    const headers = request.headers;
    // @ts-ignore
    const cookieHeader = headers["cookie"];
    // @ts-ignore
    const cookieOverrideHeader = headers["cookieOverride"];
    // @ts-ignore
    const userAgent = headers["user-agent"] || "";
    const parsedCookies = cookieHeader
        ? cookie.parse(cookieHeader)
        : cookieOverrideHeader
        ? JSON.parse(cookieOverrideHeader)
        : {};
    const atlCohort = getOrGenerateAtlCohort(parsedCookies);
    if (atlCohort) {
        data[ATL_COHORT] = atlCohort.bucketAll.index;
    }
    const {
        isTablet,
        isMobile,
        isDesktop,
        browserName,
        browserVersion,
        osName
    } = getSelectorsByUserAgent(userAgent) ?? {};
    if (isTablet) {
        data[DEVICE_TYPE] = "tablet";
    } else if (isMobile) {
        data[DEVICE_TYPE] = "mobile";
    } else if (isDesktop) {
        data[DEVICE_TYPE] = "desktop";
    } else {
        data[DEVICE_TYPE] = "other";
    }
    data.platform = "uip";
    data.browserName = browserName;
    data.browserVersion = browserVersion;
    data.browserOS = osName;
    data.queryParams = location.search;
    data.requestPath = location.pathname;
    data.referer = request.referrer;
    data.locale = parseRoute(globalThis.location).locale;

    const cloudSessionToken =
        parsedCookies["cloud.session.token"] ||
        parsedCookies["cloud.session.token.stg"];
    data.loginUserDetected = cloudSessionToken !== undefined;
    return data as UserAttributes;
};
